import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Loader, ShoppingBag } from 'lucide-react';
import { supabase } from '../lib/supabase';

const CheckoutSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const updateOrderStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // Get the most recent pending order for the user
        const { data: order, error: orderError } = await supabase
          .from('orders')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (orderError) throw orderError;
        if (!order) throw new Error('No pending order found');

        // Update order status to completed
        const { error: updateError } = await supabase
          .from('orders')
          .update({ 
            status: 'completed',
            payment_status: 'completed',
            payment_timestamp: new Date().toISOString()
          })
          .eq('id', order.id);

        if (updateError) throw updateError;

        // Wait for 3 seconds before redirecting
        setTimeout(() => {
          navigate('/account');
        }, 3000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to process order');
      } finally {
        setLoading(false);
      }
    };

    updateOrderStatus();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">An error occurred: {error}</div>
          <button
            onClick={() => navigate('/shop')}
            className="bg-yellow-500 text-white px-6 py-2 rounded-lg hover:bg-yellow-600 flex items-center justify-center gap-2"
          >
            <ShoppingBag className="h-5 w-5" />
            Return to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold mb-4">Thank You for Your Order!</h1>
          <p className="text-gray-600 mb-6">
            Your order has been successfully processed. You will receive an email confirmation shortly.
          </p>
          <div className="space-y-4">
            <button
              onClick={() => navigate('/account')}
              className="w-full bg-yellow-500 text-white px-6 py-3 rounded-lg hover:bg-yellow-600"
            >
              View Order Status
            </button>
            <button
              onClick={() => navigate('/shop')}
              className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;